import { Injectable } from '@angular/core';
import { DashboardProfileContextService } from '@rma/authentication/public/user';
import { VerticalType } from '@rma/common-ui-domain/types';
import { rmaShareReplay } from '@rma/generic/util/operators/rma-share-replay';
import { map, Observable, switchMap } from 'rxjs';
import type {
  ExternalReviewSource,
  ExternalReviewSourceType,
  ImportableReview,
  ImportableReviewResponse,
  ImportedReviewResponse,
  ImportReviewRequest,
  LinkReviewOppsCount,
} from './domain';
import { PropertySuggestion } from './domain/property-suggestion.model';
import { PropertySuggestionRequest } from './domain/property-suggestions-request.model';
import { ExternalReviewsApiService } from './external-reviews-api.service';

@Injectable({
  providedIn: 'root',
})
export class ExternalReviewsContextService {
  constructor(
    private readonly externalReviewsApiService: ExternalReviewsApiService,
    private readonly profileContextService: DashboardProfileContextService,
  ) {}

  public getImportExternalReviewCount(verticalType: VerticalType): Observable<LinkReviewOppsCount> {
    return this.profileContextService.profileIdentifier$.pipe(
      switchMap((profileIdWithVertical) =>
        this.externalReviewsApiService.getImportExternalReviewCount(
          verticalType,
          profileIdWithVertical.profileType,
          profileIdWithVertical.profileCode,
        ),
      ),
    );
  }

  public getOnlyImportableReviewCount(verticalType: VerticalType, externalReviewSources: ExternalReviewSourceType[]): Observable<number> {
    return this.profileContextService.profileIdentifier$.pipe(
      switchMap((profileIdWithVertical) =>
        this.externalReviewsApiService.getOnlyImportableReviewCount(
          verticalType,
          profileIdWithVertical.profileType,
          profileIdWithVertical.profileCode,
          externalReviewSources,
        ),
      ),
    );
  }

  public getExternalReviewSources(verticalType: VerticalType): Observable<ExternalReviewSource[]> {
    return this.profileContextService.profileIdentifier$.pipe(
      switchMap((profileIdWithVertical) =>
        this.externalReviewsApiService.getExternalReviewSources(
          verticalType,
          profileIdWithVertical.profileType,
          profileIdWithVertical.profileCode,
        ),
      ),
    );
  }

  public getAvailableSources(verticalType: VerticalType): Observable<ExternalReviewSourceType[]> {
    return this.getExternalReviewSources(verticalType).pipe(
      map((x) => x.filter((s) => s.isAvailable).map((s) => s.externalReviewSourceEnum)),
      rmaShareReplay(),
    );
  }

  public getExternalReviews(
    verticalType: VerticalType,
    externalReviewSources?: ExternalReviewSourceType[],
  ): Observable<ImportableReviewResponse[]> {
    return this.profileContextService.profileIdentifier$.pipe(
      switchMap((profileIdWithVertical) =>
        this.externalReviewsApiService.getExternalReviews(
          verticalType,
          profileIdWithVertical.profileType,
          profileIdWithVertical.profileCode,
          externalReviewSources,
        ),
      ),
    );
  }

  public deleteExternalReview(
    verticalType: VerticalType,
    externalReviewId: string,
    externalReviewSource: ExternalReviewSourceType,
  ): Observable<void> {
    return this.profileContextService.profileIdentifier$.pipe(
      switchMap((profileIdWithVertical) =>
        this.externalReviewsApiService.deleteExternalReview(
          verticalType,
          profileIdWithVertical.profileType,
          profileIdWithVertical.profileCode,
          externalReviewId,
          externalReviewSource,
        ),
      ),
    );
  }

  public getExternalReview(
    verticalType: VerticalType,
    externalReviewId: string,
    externalReviewSource: ExternalReviewSourceType,
  ): Observable<ImportableReviewResponse> {
    return this.profileContextService.profileIdentifier$.pipe(
      switchMap((profileIdWithVertical) =>
        this.externalReviewsApiService.getExternalReview(
          verticalType,
          profileIdWithVertical.profileType,
          profileIdWithVertical.profileCode,
          externalReviewId,
          externalReviewSource,
        ),
      ),
    );
  }
  public getPropertySuggestions(
    verticalType: VerticalType,
    propertySuggestionRequest: PropertySuggestionRequest,
  ): Observable<PropertySuggestion[]> {
    return this.profileContextService.profileIdentifier$.pipe(
      switchMap((profileIdWithVertical) =>
        this.externalReviewsApiService.getPropertySuggestions(
          verticalType,
          profileIdWithVertical.profileType,
          profileIdWithVertical.profileCode,
          propertySuggestionRequest,
        ),
      ),
    );
  }
  public removePropertySuggestion(
    verticalType: VerticalType,
    externalReviewId: string,
    externalReviewSource: ExternalReviewSourceType,
    campaignId: string,
  ): Observable<void> {
    return this.profileContextService.profileIdentifier$.pipe(
      switchMap((profileIdWithVertical) =>
        this.externalReviewsApiService.removePropertySuggestion(
          verticalType,
          profileIdWithVertical.profileType,
          profileIdWithVertical.profileCode,
          externalReviewId,
          externalReviewSource,
          campaignId,
        ),
      ),
    );
  }

  public linkPublishReview(
    verticalType: VerticalType,
    request: Omit<ImportReviewRequest, 'reviewExternalIdentifiers'>,
    review: ImportableReview,
  ): Observable<ImportedReviewResponse> {
    const importRequest: ImportReviewRequest = {
      ...request,
      reviewTitle: request.reviewTitle ?? review.title ?? this.createReviewTitleFromText(review.review),
      reviewExternalIdentifiers: [
        {
          externalReviewSource: review.externalReviewSource,
          externalIdentifier: review.externalReviewId,
        },
      ],
    };

    return this.importExternalReview(verticalType, importRequest);
  }

  public createReviewTitleFromText(reviewText: string): string {
    if (!reviewText) {
      return '';
    }

    return reviewText.split('.')[0].substr(0, 100);
  }

  public importExternalReview(verticalType: VerticalType, request: ImportReviewRequest): Observable<ImportedReviewResponse> {
    return this.profileContextService.profileIdentifier$.pipe(
      switchMap((profileIdWithVertical) =>
        this.externalReviewsApiService.importExternalReview(
          verticalType,
          profileIdWithVertical.profileType,
          profileIdWithVertical.profileCode,
          request,
        ),
      ),
    );
  }

  public getHasAnyImportableReviews(verticalType: VerticalType): Observable<boolean> {
    return this.profileContextService.profileIdentifier$.pipe(
      switchMap((profileIdWithVertical) =>
        this.externalReviewsApiService.getHasImportableReviews(
          verticalType,
          profileIdWithVertical.profileType,
          profileIdWithVertical.profileCode,
        ),
      ),
    );
  }
}
